/**
 * The replacement queue: bookings that lost their pandit, and the three ways back.
 *
 * **Ordered by the ceremony, not by the cancellation.** The server sorts it, and this screen does
 * not re-sort. A booking cancelled an hour ago for tomorrow morning is more urgent than one
 * cancelled last week for next month, and a queue by age would bury it under things that can wait.
 *
 * **The three actions are offered in waterfall order, always** (doc 22 §5.5): promote the standing
 * backup, then re-dispatch, then give up and refund. Each step is slower and worse for the devotee
 * than the one before, so the order is not a preference and the screen does not present them as
 * equals.
 *
 * **The backup column is the one to read first.** `standing` means this is a two-second fix;
 * `missing` means the standby loop never found anybody, and this booking is one more disruption
 * away from a refund.
 */
import { useState } from 'react';
import { Note, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useDispatchHistory,
  usePromoteBackup,
  useReassignments,
  useRedispatch,
  useReplacementFailed,
  type ReassignmentRow,
} from '../lib/api/hooks/dispatch';
import { ceremonyAt, statusLabel, statusTone } from './DispatchBoard';

const PAGE_SIZE = 50;

/**
 * How much trouble one row is in, read off the backup and the clock together.
 *
 * A standing backup makes almost any lead time survivable; no backup and three hours left is the
 * row an operator should be phoning about right now.
 */
export function rowTone(backup: string, minutesToStart: number): 'error' | 'warn' | 'success' {
  if (backup === 'standing') return 'success';
  if (backup === 'missing' && minutesToStart < 240) return 'error';
  return 'warn';
}

export function backupLabel(backup: string): string {
  if (backup === 'standing') return 'standby ready';
  if (backup === 'invited') return 'standby asked';
  return 'no standby';
}

/** "3h 20m" reads faster than a minute count when somebody is deciding what to do first. */
export function untilCeremony(minutes: number): string {
  const sign = minutes < 0 ? '−' : '';
  const abs = Math.abs(minutes);
  if (abs < 60) return `${sign}${abs}m`;
  if (abs < 60 * 48) return `${sign}${Math.floor(abs / 60)}h ${abs % 60}m`;
  return `${sign}${Math.floor(abs / 1440)}d`;
}

/** The cause, in the words an operator would use rather than the database's. */
export function causeLabel(reason: string, initiatedBy: string): string {
  if (reason === 'quality_pull') return 'pulled by ops';
  if (reason === 'no_show') return 'no-show';
  return initiatedBy === 'pandit' ? 'pandit cancelled' : reason.replaceAll('_', ' ');
}

export function Replacements() {
  const { scope, toast } = useConsole();
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState<ReassignmentRow | null>(null);
  const [confirm, setConfirm] = useState<'promote' | 'redispatch' | 'failed' | null>(null);

  const queue = useReassignments({ page, page_size: PAGE_SIZE });
  const history = useDispatchHistory(openRow?.booking_id ?? null);
  const promote = usePromoteBackup();
  const redispatch = useRedispatch();
  const giveUp = useReplacementFailed();

  const rows = queue.data?.items ?? [];
  const total = queue.data?.total ?? 0;
  const fail = (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) });
  const done = (message: string) => {
    setConfirm(null);
    setOpenRow(null);
    toast({ tone: 'info', message });
  };

  return (
    <>
      {queue.isPending && <LoadingState message="Reading the replacement queue…" />}
      {queue.isError && (
        <ErrorState title="Could not read the queue" cause={errorMessage(queue.error)} />
      )}
      {queue.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="repeat"
          title="Nothing needs replacing"
          body="Every booking still has the pandit it started with."
          inline
        />
      )}

      {rows.length > 0 && (
        <DataTable
          density="compact"
          onRowClick={(row) =>
            // The id was rendered from `rows` in this same pass, so the lookup cannot miss.
            // A `?? null` fallback here would be a branch no click can reach.
            setOpenRow(rows.find((r) => r.booking_id === Number(row.id))!)
          }
          footer={
            <>
              <span>
                {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + rows.length} of {total}
              </span>
              <span style={{ flex: 1 }} />
              <ScopeAction
                scope={scope}
                need="ops_dispatch"
                size="sm"
                variant="ghost"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </ScopeAction>
              <ScopeAction
                scope={scope}
                need="ops_dispatch"
                size="sm"
                variant="ghost"
                disabled={page * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </ScopeAction>
            </>
          }
          columns={[
            { key: 'code', label: 'Booking', mono: true },
            { key: 'when', label: 'Ceremony', sort: 'asc' },
            { key: 'clock', label: 'Time left', align: 'right', mono: true },
            { key: 'cause', label: 'What happened' },
            { key: 'backup', label: 'Standby' },
            { key: 'episodes', label: 'Attempts', align: 'right', mono: true },
          ]}
          rows={rows.map((r) => ({
            id: r.booking_id,
            code: r.booking_public_code,
            when: ceremonyAt(r.scheduled_start_at),
            clock: untilCeremony(r.minutes_to_start),
            cause: causeLabel(r.reason, r.initiated_by),
            backup: (
              <StatusChip
                tone={rowTone(r.backup, r.minutes_to_start)}
                label={backupLabel(r.backup)}
              />
            ),
            episodes: r.episodes,
          }))}
        />
      )}

      {openRow && (
        <Section
          title={`${openRow.booking_public_code} · ${statusLabel(openRow.status)}`}
          note={`${causeLabel(openRow.reason, openRow.initiated_by)} · ${untilCeremony(openRow.minutes_to_start)} to the ceremony`}
          actions={
            <StatusChip tone={statusTone(openRow.status)} label={statusLabel(openRow.status)} />
          }
        >
          {openRow.notes && (
            <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
              “{openRow.notes}”
            </p>
          )}
          {history.isSuccess && (
            <p style={{ margin: 0, fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
              {history.data.batches.length} wave
              {history.data.batches.length === 1 ? '' : 's'} sent for this booking so far.
            </p>
          )}

          {/* Waterfall order, always. Each step is slower and worse for the devotee than
              the one above it, so they are not presented as equals. */}
          <div style={{ display: 'flex', gap: 'var(--sp-md)', flexWrap: 'wrap' }}>
            <ScopeAction
              scope={scope}
              need="ops_dispatch"
              size="sm"
              icon="user-check"
              disabled={openRow.backup !== 'standing'}
              onClick={() => setConfirm('promote')}
            >
              Put the standby in
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="ops_dispatch"
              size="sm"
              variant="secondary"
              icon="repeat"
              onClick={() => setConfirm('redispatch')}
            >
              Look for somebody else
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="ops_dispatch"
              size="sm"
              variant="secondary"
              icon="circle-alert"
              onClick={() => setConfirm('failed')}
            >
              Give up and refund
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="ops_dispatch"
              size="sm"
              variant="ghost"
              onClick={() => setOpenRow(null)}
            >
              Close
            </ScopeAction>
          </div>

          {openRow.backup !== 'standing' && (
            <Note>
              No standby has agreed to this booking, so there is nobody to promote — the standby
              loop {openRow.backup === 'invited' ? 'is still waiting on an answer' : 'found nobody'}.
            </Note>
          )}
        </Section>
      )}

      <ConfirmAction
        open={confirm === 'promote'}
        title="Put the standby in?"
        phrase="PROMOTE"
        consequences={[
          'The standby becomes the pandit and the booking is confirmed again — the devotee is told the pandit changed, same puja, same time, same price.',
          'Their availability is re-checked first: a standby whose own calendar filled is released instead, and you will be told so.',
        ]}
        confirmLabel="Promote"
        onCancel={() => setConfirm(null)}
        onConfirm={() =>
          openRow &&
          promote.mutate(
            { bookingId: openRow.booking_id },
            {
              onSuccess: (result) =>
                done(
                  result.outcome === 'promoted'
                    ? 'Standby promoted — the booking is confirmed again.'
                    : `Not promoted: ${result.outcome.replaceAll('_', ' ')}. Try looking again.`,
                ),
              onError: fail,
            },
          )
        }
      />

      <ConfirmAction
        open={confirm === 'redispatch'}
        title="Look for somebody else?"
        phrase="REDISPATCH"
        consequences={[
          'A fresh wave goes to qualified pandits who were not offered this booking before.',
          'Refused if the ceremony is too close for anybody to accept in time, or if this booking has already lost more than one pandit.',
        ]}
        confirmLabel="Send a wave"
        onCancel={() => setConfirm(null)}
        onConfirm={() =>
          openRow &&
          redispatch.mutate(
            { bookingId: openRow.booking_id },
            {
              onSuccess: (wave) =>
                done(
                  wave.batch
                    ? `Wave sent to ${wave.batch.offer_count}.`
                    : `Nothing sent — ${wave.reason?.replaceAll('_', ' ')}.`,
                ),
              onError: fail,
            },
          )
        }
      />

      <ConfirmAction
        open={confirm === 'failed'}
        title="Give up on finding a replacement?"
        phrase="REFUND"
        tone="error"
        reason="What was tried, and why this is the end of it"
        consequences={[
          'The booking ends as cancelled by the pandit — never by the devotee, who did nothing.',
          'The refund path opens, and every standby still waiting on this booking is released.',
          'This cannot be undone. If there is any chance left, look again first.',
        ]}
        confirmLabel="Give up"
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) =>
          openRow &&
          giveUp.mutate(
            { bookingId: openRow.booking_id, reason },
            {
              onSuccess: () => done('Recorded. The refund path is open.'),
              onError: fail,
            },
          )
        }
      />

      <Note>
        Ordered by the ceremony, soonest first — the server decides that, not this screen. Standby
        promotion, a fresh wave, then a refund: each step is slower and worse for the devotee than
        the one before.
      </Note>
    </>
  );
}
